import { FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query'

import { getEnrichedSortParam, transformResponseWithPagination } from '@helpers'
import { ENTITY_TYPES_REQUEST, URLS } from '@constants'
import {
  GETAllEntity,
  GETDefaultViewInSAParams,
  GETEntity,
  GETObjectData,
  GETObjectDataParams,
  GETObjectDataParamsEnriched,
  GETObjectDataRecordEnrichedParams,
  GETOptionGroupType,
  ObjectDataRecord,
  POSTCopyEntity,
  POSTEntityDTO,
  POSTObjectDataFilter,
  POSTOptionGroupType,
  UpdateDefaultViewInSAParams,
  ViewShortDTO,
} from '@types'

import { commonAPI } from './common.api'
import { fetchEnrichedEntityConfigById } from './helpers'

export const viewApi = commonAPI.injectEndpoints({
  endpoints: build => ({
    fetchAllView: build.query<GETAllEntity, GETObjectDataParams | void>({
      query: params => ({
        url: URLS.FRONT_CFG + '/view',
        params: {
          page: params?.page,
          size: params?.size,
          sort: params?.sort,
          objectCode: params?.objectCode,
          searchPattern: params?.searchPattern,
          viewType: params?.viewType || ENTITY_TYPES_REQUEST.VIEW,
        },
      }),
      transformResponse: (output: ViewShortDTO[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<ViewShortDTO[]>(output, meta),
      providesTags: ['AllViews'],
    }),
    fetchAllFormIntegrationViews: build.query<GETAllEntity, string | void>({
      query: objectCode => ({
        url: URLS.FRONT_CFG + `/view/integration/${objectCode}`,
      }),
      transformResponse: (output: ViewShortDTO[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<ViewShortDTO[]>(output, meta),
      providesTags: ['FormIntegratedViews'],
    }),
    fetchAllViewUncertain: build.query<GETAllEntity, GETObjectDataParams | void>({
      query: params => ({
        url: URLS.FRONT_CFG + '/view/uncertain',
        params: {
          page: params?.page,
          size: params?.size,
          sort: params?.sort,
          viewType: params?.viewType || ENTITY_TYPES_REQUEST.VIEW,
        },
      }),
      transformResponse: (output: ViewShortDTO[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<ViewShortDTO[]>(output, meta),
      providesTags: ['UncertainViews'],
    }),
    fetchViewById: build.query<GETEntity, string | number>({
      queryFn: fetchEnrichedEntityConfigById,
    }),
    fetchViewColumnById: build.query<GETEntity, number>({
      query: columnId => ({
        url: URLS.FRONT_CFG + `/view/column/${columnId}`,
      }),
      providesTags: [],
      keepUnusedDataFor: 0,
    }),
    fetchView: build.query<
      GETEntity,
      {
        code: string | void
        viewType: ENTITY_TYPES_REQUEST | string | null | void
      }
    >({
      query: ({ code, viewType }) => ({
        url: URLS.FRONT_CFG + `/view/code/${code}?viewType=${viewType}`,
      }),
      providesTags: ['SingleView', 'Parameter'],
    }),
    fetchViewByCode: build.query<
      GETEntity,
      {
        code: string | void
        viewType: ENTITY_TYPES_REQUEST | string | null | void
      }
    >({
      query: ({ code, viewType }) => ({
        url: URLS.FRONT_CFG + `/view/code/${code}?viewType=${viewType}`,
      }),
      providesTags: ['SingleView', 'Parameter', 'DefaultSA'],
    }),
    createView: build.mutation<GETEntity, POSTEntityDTO>({
      query: view => ({
        url: URLS.FRONT_CFG + '/view',
        method: 'POST',
        body: view,
      }),
      invalidatesTags: ['SingleView', 'UncertainViews', 'AllViews'],
    }),
    updateView: build.mutation<GETEntity, POSTEntityDTO>({
      query: view => ({
        url: URLS.FRONT_CFG + '/view',
        method: 'POST',
        body: view,
      }),
      invalidatesTags: ['SingleView', 'UncertainViews', 'AllViews'],
    }),
    deleteView: build.mutation<GETEntity, number | string>({
      query: id => ({
        url: URLS.FRONT_CFG + `/view/id/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'Menu',
        'UncertainViews',
        'GroupViews',
        'AllViews',
        'SingleForm',
        'DefaultSA',
      ],
    }),
    deleteViewByCode: build.mutation<GETEntity, string>({
      query: code => ({
        url: URLS.FRONT_CFG + `/view/code/${code}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Menu', 'UncertainViews', 'GroupViews', 'AllViews', 'SingleForm'],
    }),
    fetchViewGroups: build.query<
      GETOptionGroupType[],
      { viewType: ENTITY_TYPES_REQUEST | string | null | void }
    >({
      query: params => ({
        url: URLS.FRONT_CFG + '/group/view',
        params,
      }),
      providesTags: ['GroupViews'],
    }),
    updateViewGroups: build.mutation<
      GETOptionGroupType[],
      { groups: POSTOptionGroupType[]; viewType: ENTITY_TYPES_REQUEST | string | null | void }
    >({
      query: ({ groups, viewType }) => ({
        url: URLS.FRONT_CFG + '/group/view',
        method: 'POST',
        body: groups,
        params: { viewType },
      }),
      invalidatesTags: ['UncertainViews', 'GroupViews'],
    }),
    fetchUndefinedViewsByFormCode: build.query<GETEntity[], string>({
      query: formCode => ({
        url: URLS.FRONT_CFG + `/view/formCode/${formCode}/undefined`,
      }),
      providesTags: ['UndefinedViews'],
    }),
    copyView: build.mutation<GETEntity, POSTCopyEntity>({
      query: viewData => ({
        url: URLS.FRONT_CFG + '/view/copy',
        method: 'POST',
        body: viewData,
      }),
      invalidatesTags: ['UncertainViews', 'AllViews'],
    }),
    updateDefaultViewSA: build.mutation<GETEntity, UpdateDefaultViewInSAParams>({
      query: ({ contextId, viewId }) => ({
        url: URLS.FRONT_CFG + '/context/default_view',
        method: 'POST',
        body: {
          id: contextId,
          viewId,
        },
      }),
      invalidatesTags: ['DefaultSA'],
    }),
    fetchDefaultViewSA: build.query<GETEntity, GETDefaultViewInSAParams>({
      query: ({ contextId }) => ({
        url: URLS.FRONT_CFG + `/context/id/${contextId}/default_view`,
        method: 'GET',
      }),
      providesTags: ['DefaultSA'],
    }),
    // Загрузка данных для VIEW
    fetchObjectDataFilterByCodeEnriched: build.mutation<GETObjectData, GETObjectDataParamsEnriched>(
      {
        query: ({
          code,
          page,
          size,
          sort,
          filter,
          viewId,
          contextId,
          sortKeysForQuickSearch,
          searchValue,
          searchRule,
          body,
        }) => ({
          url: URLS.FRONT_API + `/data/view/${viewId}?${getEnrichedSortParam(sort)}`,
          method: 'POST',
          params: {
            code,
            page,
            size,
            viewId,
            contextId,
            filter,
            searchValue,
            searchRule,
            sortKeysForQuickSearch,
          },
          body,
        }),
        extraOptions: {
          showNotification: false,
        },
        transformResponse: (output: ObjectDataRecord[], meta: FetchBaseQueryMeta) =>
          transformResponseWithPagination<ObjectDataRecord[]>(output, meta),
        // providesTags: ['ObjectData'],
      }
    ),
    // Загрузка данных для ListControl, DropDownList и DropDownListWindow
    fetchListControlOrDropdownObjectDataEnriched: build.mutation<
      GETObjectData,
      GETObjectDataParamsEnriched
    >({
      query: ({
        contextId,
        page,
        size,
        sort,
        formObjectCode,
        elementId,
        valueId,
        fieldId,
        columnId,
        dropDownEntityType,
        formObjectId,
        formObjectInternalId,
        viewId,
        body,
        filter,
        searchValue,
        searchRule,
        formFields,
        sortKeysForQuickSearch,
      }) => ({
        url: URLS.FRONT_API + `/data/dropDown?${getEnrichedSortParam(sort)}`,
        method: 'POST',
        params: {
          contextId,
          viewId,
          dropDownEntityType,
          page,
          size,
          formObjectCode,
          formObjectId,
          formObjectInternalId,
          elementId,
          valueId,
          formFields,
          fieldId,
          columnId,
          filter,
          searchValue,
          searchRule,
          sortKeysForQuickSearch,
        },
        body: {
          ...body,
        },
      }),
      extraOptions: {
        showNotification: false,
      },
      transformResponse: (output: ObjectDataRecord[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<ObjectDataRecord[]>(output, meta),
      // providesTags: ['ObjectData'],
    }),
    // Используется для загрузки объекта после WS
    fetchObjectDataRecordEnrichedById: build.mutation<
      ObjectDataRecord[],
      GETObjectDataRecordEnrichedParams
    >({
      query: ({
        ids,
        viewId,
        body,
        searchValue,
        searchRule,
        sortKeysForQuickSearch,
        contextId,
        filter,
      }) => ({
        url: URLS.FRONT_API + `/data/view/${viewId}/id`,
        method: 'POST',
        params: {
          ids,
          viewId,
          contextId,
          searchValue,
          searchRule,
          sortKeysForQuickSearch,
          filter,
        },
        body,
      }),
      extraOptions: {
        showNotification: false,
      },
    }),
    fetchSAFiltersAsRSQL: build.mutation<string, POSTObjectDataFilter>({
      query: body => ({
        url: URLS.FRONT_API + '/data/searchAssistant',
        method: 'POST',
        body,
        responseHandler: response => response.text(),
      }),
    }),
  }),
})

export const {
  useDeleteViewMutation,
  useDeleteViewByCodeMutation,
  useFetchAllViewQuery,
  useFetchAllFormIntegrationViewsQuery,
  useFetchViewQuery,
  useFetchViewByCodeQuery,
  useFetchViewColumnByIdQuery,
  useCreateViewMutation,
  useUpdateViewMutation,
  useFetchAllViewUncertainQuery,
  useFetchViewGroupsQuery,
  useUpdateViewGroupsMutation,
  useFetchUndefinedViewsByFormCodeQuery,
  useCopyViewMutation,
  useUpdateDefaultViewSAMutation,
  useFetchDefaultViewSAQuery,
  useFetchViewByIdQuery,
  useLazyFetchViewByIdQuery,
  useFetchObjectDataFilterByCodeEnrichedMutation,
  useFetchObjectDataRecordEnrichedByIdMutation,
  useFetchListControlOrDropdownObjectDataEnrichedMutation,
  useFetchSAFiltersAsRSQLMutation,
} = viewApi
